/** Shown instead of a clock time when a same-day event ends after midnight (e.g. 1 AM, 2 AM). */
export const LATE_NIGHT_END_TIME_LABEL = "Late night";

export type EventTimeDisplayInput = {
  isSameDayEvent?: boolean;
  eventTime?: string | null;
  endTime?: string | null;
};

/** Minutes since midnight from `HH:mm` or `HH:mm:ss`; null when missing or invalid. */
function parseTimeToMinutes(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(trimmed);
  if (!match) return null;

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }

  return hour * 60 + minute;
}

/**
 * Same-day events that run past midnight often end in the early morning (1 AM, 2 AM, etc.).
 * Keep in sync with API `eventTimeDisplayUtil`.
 */
export function isLateNightSameDayEnd(event: EventTimeDisplayInput): boolean {
  if (event.isSameDayEvent !== true) return false;

  const endMinutes = parseTimeToMinutes(event.endTime);
  if (endMinutes === null) return false;

  const endHour = Math.floor(endMinutes / 60);
  if (endHour >= 12) return false;

  const startMinutes = parseTimeToMinutes(event.eventTime);
  if (startMinutes === null) return true;

  const crossesMidnight = endMinutes < startMinutes;
  const startsInEvening = startMinutes >= 12 * 60;

  return crossesMidnight || startsInEvening;
}

export function formatTo12Hour(time?: string | null): string {
  const minutes = parseTimeToMinutes(time);
  if (minutes === null) return "";

  const hour24 = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 || 12;

  return `${hour12}:${minute.toString().padStart(2, "0")} ${period}`;
}

export function formatEventEndTimeLabel(event: EventTimeDisplayInput): string {
  if (isLateNightSameDayEnd(event)) return LATE_NIGHT_END_TIME_LABEL;
  return formatTo12Hour(event.endTime);
}

/** Full range label, e.g. `8:00 PM - Late night`. Keep in sync with API `eventTimeDisplayUtil`. */
export function formatEventTimeRange(event: EventTimeDisplayInput): string {
  const hasStart =
    event.eventTime != null && String(event.eventTime).trim() !== "";
  const hasEnd = event.endTime != null && String(event.endTime).trim() !== "";

  if (!hasStart) return "";

  const start = formatTo12Hour(event.eventTime);
  if (!start) return "";

  // Start only when end time is missing.
  if (!hasEnd) return start;

  const end = formatEventEndTimeLabel(event);
  if (!end) return start;

  return `${start} - ${end}`;
}
